import React, { useCallback, useEffect, useMemo, useState } from 'react';
import apiService from '../../services/api';
import {
  Tier1OrderUserDecision,
  Tier1RebalanceProposal,
  Tier1Workbench,
} from '../../types/tier1Portfolio';
import { Card, CardContent, CardHeader, CardTitle } from '../Card';
import Tier1TradeRow from './Tier1TradeRow';
import { fmtUsd } from './format';

interface Props {
  accountId: string;
  tierId: string;
  autoApproveEffective: boolean;
  hasActivePlan: boolean;
  /** Registry status by sleeveKey from implementation */
  registryBySleeve: Map<string, string | undefined>;
  onRefresh: () => void | Promise<void>;
  busyExternal?: boolean;
}

const Tier1ExecutionWorkbench: React.FC<Props> = ({
  accountId,
  tierId,
  autoApproveEffective,
  hasActivePlan,
  registryBySleeve,
  onRefresh,
  busyExternal,
}) => {
  const [workbench, setWorkbench] = useState<Tier1Workbench | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [decisions, setDecisions] = useState<Map<number, Tier1OrderUserDecision>>(new Map());
  const [historyOpen, setHistoryOpen] = useState(false);

  const loadWorkbench = useCallback(async () => {
    if (!hasActivePlan) {
      setWorkbench(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const data = await apiService.getTier1Workbench(accountId, tierId);
      setWorkbench(data);
      const proposal =
        data.pendingApprovalProposal ??
        (data.activeProposal?.status === 'proposed' ? data.activeProposal : null);
      if (proposal?.status === 'proposed') {
        const next = new Map<number, Tier1OrderUserDecision>();
        proposal.orders.forEach((o, i) => {
          next.set(i, o.userDecision ?? 'pending');
        });
        setDecisions(next);
      } else {
        setDecisions(new Map());
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load workbench');
    } finally {
      setLoading(false);
    }
  }, [accountId, tierId, hasActivePlan]);

  useEffect(() => {
    void loadWorkbench();
  }, [loadWorkbench]);

  const activeProposal = workbench?.activeProposal ?? null;
  const approvalProposal =
    workbench?.pendingApprovalProposal ??
    (activeProposal?.status === 'proposed' ? activeProposal : null);
  const executingProposals = useMemo(() => {
    const recent = workbench?.recentProposals ?? [];
    const open = recent.filter(
      (proposal) => proposal.status === 'approved' && !proposal.tradesExecuted,
    );
    if (
      activeProposal?.status === 'approved' &&
      !activeProposal.tradesExecuted &&
      !open.some((proposal) => proposal._id === activeProposal._id)
    ) {
      open.unshift(activeProposal);
    }
    return open;
  }, [workbench?.recentProposals, activeProposal]);
  const executingProposal = executingProposals[0] ?? null;
  const showDecisionUi =
    !!approvalProposal &&
    approvalProposal.status === 'proposed' &&
    !autoApproveEffective &&
    approvalProposal.orders.length > 0;

  const onDecisionChange = (index: number, decision: Tier1OrderUserDecision) => {
    setDecisions((prev) => {
      const next = new Map(prev);
      next.set(index, decision);
      return next;
    });
  };

  const approveAll = () => {
    if (!approvalProposal) return;
    const next = new Map<number, Tier1OrderUserDecision>();
    approvalProposal.orders.forEach((_, i) => next.set(i, 'approved'));
    setDecisions(next);
  };

  const denyAll = () => {
    if (!approvalProposal) return;
    const next = new Map<number, Tier1OrderUserDecision>();
    approvalProposal.orders.forEach((_, i) => next.set(i, 'denied'));
    setDecisions(next);
  };

  const submitDecisions = async () => {
    if (!approvalProposal) return;
    setBusy('submit');
    setError(null);
    try {
      const payload = approvalProposal.orders.map((_, orderIndex) => ({
        orderIndex,
        decision: (decisions.get(orderIndex) === 'approved' ? 'approved' : 'denied') as
          | 'approved'
          | 'denied',
      }));
      await apiService.approveTier1RebalanceProposal(approvalProposal._id, {
        decisions: payload,
      });
      await loadWorkbench();
      await onRefresh();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Submit failed');
    } finally {
      setBusy(null);
    }
  };

  const rejectProposal = async (proposalId?: string) => {
    const id = proposalId ?? approvalProposal?._id ?? executingProposal?._id;
    if (!id) return;
    setBusy('reject');
    setError(null);
    try {
      await apiService.rejectTier1RebalanceProposal(id);
      await loadWorkbench();
      await onRefresh();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Reject failed');
    } finally {
      setBusy(null);
    }
  };

  const summary = workbench?.summary;
  const tradeOrdersByLine = workbench?.tradeOrdersByLine ?? {};

  const historyProposals = useMemo(() => {
    const recent = workbench?.recentProposals ?? [];
    const shown = new Set(
      [approvalProposal?._id, ...executingProposals.map((proposal) => proposal._id)].filter(
        (id): id is string => !!id,
      ),
    );
    return recent.filter((p) => !shown.has(p._id));
  }, [workbench?.recentProposals, approvalProposal?._id, executingProposals]);

  if (!hasActivePlan) return null;

  return (
    <div id="tier1-execution-workbench">
    <Card>
      <CardHeader>
        <CardTitle>Execution workbench</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-gray-600">
          After you approve a plan, registry slots are created. Rebalance proposals list suggested
          trades — approve or deny each line before orders are sent (unless automatic execution is
          on).
        </p>

        {loading && !workbench ? (
          <p className="text-sm text-gray-500">Loading execution state…</p>
        ) : null}

        {error ? (
          <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
            {error}
          </div>
        ) : null}

        {summary ? (
          <div className="flex flex-wrap gap-3 text-xs text-gray-700">
            <span className="rounded-full bg-gray-100 px-2 py-1">
              Open sleeves: {summary.openSleeves}
            </span>
            <span className="rounded-full bg-blue-50 px-2 py-1">
              To open: {summary.needOpen}
            </span>
            <span className="rounded-full bg-sky-50 px-2 py-1">To add: {summary.needAdd}</span>
            <span className="rounded-full bg-orange-50 px-2 py-1">
              To close/trim: {summary.needClose}
            </span>
            {summary.pendingApproval > 0 ? (
              <span className="rounded-full bg-violet-100 px-2 py-1 font-medium text-violet-900">
                Awaiting approval: {summary.pendingApproval} line(s)
              </span>
            ) : null}
            {(summary.authBlockedSleeves ?? 0) > 0 ? (
              <span className="rounded-full bg-red-50 px-2 py-1 font-medium text-red-800">
                Schwab reconnect: {summary.authBlockedSleeves}
              </span>
            ) : null}
            {(summary.failedSleeves ?? 0) > 0 ? (
              <span className="rounded-full bg-red-50 px-2 py-1 font-medium text-red-800">
                Submit failed: {summary.failedSleeves}
              </span>
            ) : null}
            {(summary.residualSleeves ?? 0) > 0 ? (
              <span className="rounded-full bg-gray-100 px-2 py-1">
                Below one share: {summary.residualSleeves}
              </span>
            ) : null}
            {summary.executionStalled ? (
              <span className="rounded-full bg-red-100 px-2 py-1 font-medium text-red-900">
                Execution stalled
              </span>
            ) : null}
          </div>
        ) : null}

        {approvalProposal ? (
          <ProposalBlock
            proposal={approvalProposal}
            tradeOrdersByLine={tradeOrdersByLine}
            registryBySleeve={registryBySleeve}
            decisions={decisions}
            showDecisionUi={showDecisionUi}
            autoApproveEffective={autoApproveEffective}
            onDecisionChange={onDecisionChange}
            approveAll={approveAll}
            denyAll={denyAll}
            submitDecisions={submitDecisions}
            rejectProposal={() => rejectProposal(approvalProposal._id)}
            showAbandonUi={false}
            busy={busy ?? (busyExternal ? 'external' : null)}
          />
        ) : null}
        {executingProposals
          .filter((proposal) => proposal._id !== approvalProposal?._id)
          .map((proposal) => (
            <ProposalBlock
              key={proposal._id}
              proposal={proposal}
              tradeOrdersByLine={tradeOrdersByLine}
              registryBySleeve={registryBySleeve}
              decisions={decisions}
              showDecisionUi={false}
              autoApproveEffective={autoApproveEffective}
              onDecisionChange={onDecisionChange}
              approveAll={approveAll}
              denyAll={denyAll}
              submitDecisions={submitDecisions}
              rejectProposal={() => rejectProposal(proposal._id)}
              showAbandonUi
              busy={busy ?? (busyExternal ? 'external' : null)}
            />
          ))}
        {!approvalProposal && executingProposals.length === 0 ? (
          <p className="text-sm text-gray-500">
            No pending rebalance proposal. Sleeve cards below show policy vs actual; a new proposal
            appears after plan approval (initial fill), daily sleeve repair, or monthly rebalance
            evaluation.
          </p>
        ) : null}

        {historyProposals.length > 0 ? (
          <div>
            <button
              type="button"
              className="text-sm font-medium text-primary-600 hover:underline"
              onClick={() => setHistoryOpen((v) => !v)}
            >
              {historyOpen ? 'Hide' : 'Show'} recent proposals ({historyProposals.length})
            </button>
            {historyOpen ? (
              <ul className="mt-2 space-y-2">
                {historyProposals.map((p) => (
                  <li
                    key={p._id}
                    className="rounded border border-gray-100 bg-gray-50 p-2 text-sm text-gray-700"
                  >
                    <span className="font-medium capitalize">{p.status}</span>
                    {p.proposalKind ? ` · ${p.proposalKind.replace('_', ' ')}` : ''}
                    {' · '}
                    {p.orders.length} order(s)
                    {p.executionErrors?.length ? (
                      <span className="text-red-700"> · errors</span>
                    ) : null}
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        ) : null}
      </CardContent>
    </Card>
    </div>
  );
};

const ProposalBlock: React.FC<{
  proposal: Tier1RebalanceProposal;
  tradeOrdersByLine: Record<string, { status: string; rejectionReason?: string }>;
  registryBySleeve: Map<string, string | undefined>;
  decisions: Map<number, Tier1OrderUserDecision>;
  showDecisionUi: boolean;
  autoApproveEffective: boolean;
  onDecisionChange: (i: number, d: Tier1OrderUserDecision) => void;
  approveAll: () => void;
  denyAll: () => void;
  submitDecisions: () => void;
  rejectProposal: () => void;
  showAbandonUi: boolean;
  busy: string | null;
}> = ({
  proposal,
  tradeOrdersByLine,
  registryBySleeve,
  decisions,
  showDecisionUi,
  showAbandonUi,
  autoApproveEffective,
  onDecisionChange,
  approveAll,
  denyAll,
  submitDecisions,
  rejectProposal,
  busy,
}) => (
  <div className="rounded-lg border border-gray-200 bg-white">
    <div className="border-b border-gray-100 px-3 py-2 text-sm">
      <span className="font-medium capitalize">{proposal.status}</span>
      {proposal.proposalKind ? (
        <span className="text-gray-500"> · {proposal.proposalKind.replace('_', ' ')}</span>
      ) : null}
      {proposal.taxEstimate?.turnoverUsd != null ? (
        <span className="text-gray-500">
          {' '}
          · Turnover ~{fmtUsd(proposal.taxEstimate.turnoverUsd)}
          {proposal.taxEstimate.turnoverCapUsd != null
            ? ` / cap ${fmtUsd(proposal.taxEstimate.turnoverCapUsd)}`
            : ''}
        </span>
      ) : null}
      {proposal.orders.length > 0 ? (
        <span className="text-gray-500"> · {proposal.orders.length} line(s)</span>
      ) : null}
    </div>
    {proposal.taxEstimate?.clippedByTurnover ? (
      <p className="border-b border-amber-100 bg-amber-50 px-3 py-2 text-xs text-amber-900">
        Turnover cap clipped this proposal — not all sleeves could be traded in one batch. Approve
        and submit, then expect another proposal or retry for remaining sleeves.
      </p>
    ) : null}
    {proposal.quoteSkippedSymbols && proposal.quoteSkippedSymbols.length > 0 ? (
      <p className="border-b border-amber-100 bg-amber-50 px-3 py-2 text-xs text-amber-900">
        No Schwab quote for: {proposal.quoteSkippedSymbols.join(', ')} — those sleeves were omitted
        from this batch. Retry after quotes are available.
      </p>
    ) : null}
    {proposal.executionHold?.reason === 'schwab_auth' ? (
      <p className="border-b border-red-100 bg-red-50 px-3 py-2 text-xs text-red-900">
        Schwab authentication is blocking this proposal. Reconnect Schwab and the unsent lines
        resume automatically. {proposal.executionHold.message}
      </p>
    ) : null}
    {proposal.executionPhase === 'queued' && proposal.executionHold?.reason !== 'schwab_auth' ? (
      <p className="border-b border-sky-100 bg-sky-50 px-3 py-2 text-xs text-sky-900">
        Approved — orders are queued and will submit from 15 minutes after the US regular session
        open (NYSE, Mon–Fri).
      </p>
    ) : null}
    {proposal.executionPhase === 'monitoring' && proposal.status === 'approved' ? (
      <p className="border-b border-violet-100 bg-violet-50 px-3 py-2 text-xs text-violet-900">
        Working at broker — partial fills or retries may still be in progress. Check line status
        below.
      </p>
    ) : null}
    {proposal.stalledSince ? (
      <p className="border-b border-red-100 bg-red-50 px-3 py-2 text-xs text-red-900">
        Execution stalled since {new Date(proposal.stalledSince).toLocaleDateString()} — counted
        from when the proposal was queued, not the latest retry. Review errors or abandon execution
        below.
      </p>
    ) : null}
    <div className="overflow-x-auto">
      <table className="min-w-full text-left">
        <thead className="bg-gray-50 text-xs uppercase text-gray-500">
          <tr>
            <th className="px-2 py-2">Sleeve</th>
            <th className="px-2 py-2">Symbol</th>
            <th className="px-2 py-2">Side</th>
            <th className="px-2 py-2 text-right">Qty</th>
            <th className="px-2 py-2 text-right">~$</th>
            <th className="px-2 py-2">Registry</th>
            <th className="px-2 py-2">Order</th>
            {showDecisionUi ? <th className="px-2 py-2">Decision</th> : null}
            {!showDecisionUi && proposal.status !== 'proposed' ? (
              <th className="px-2 py-2">Decision</th>
            ) : null}
          </tr>
        </thead>
        <tbody>
          {proposal.orders.map((order, orderIndex) => (
            <Tier1TradeRow
              key={`${order.symbol}-${order.side}-${orderIndex}`}
              proposalId={proposal._id}
              orderIndex={orderIndex}
              order={order}
              registryStatus={registryBySleeve.get(order.sleeveKey ?? '')}
              tradeStatus={tradeOrdersByLine[`${proposal._id}:${orderIndex}`]}
              decision={
                showDecisionUi
                  ? decisions.get(orderIndex) ?? order.userDecision ?? 'pending'
                  : order.userDecision ?? 'pending'
              }
              onDecisionChange={onDecisionChange}
              readOnly={!showDecisionUi}
            />
          ))}
        </tbody>
      </table>
    </div>
    {proposal.executionErrors?.length ? (
      <ul className="list-disc px-4 py-2 text-xs text-red-700">
        {proposal.executionErrors.map((e) => (
          <li key={e}>{e}</li>
        ))}
      </ul>
    ) : null}
    {showDecisionUi ? (
      <div className="flex flex-wrap gap-2 border-t border-gray-100 px-3 py-3">
        <button
          type="button"
          onClick={approveAll}
          disabled={!!busy}
          className="rounded border border-gray-300 bg-white px-3 py-1.5 text-sm hover:bg-gray-50 disabled:opacity-50"
        >
          Approve all
        </button>
        <button
          type="button"
          onClick={denyAll}
          disabled={!!busy}
          className="rounded border border-gray-300 bg-white px-3 py-1.5 text-sm hover:bg-gray-50 disabled:opacity-50"
        >
          Deny all lines
        </button>
        <button
          type="button"
          onClick={() => void submitDecisions()}
          disabled={!!busy}
          className="rounded bg-green-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-50"
        >
          {busy === 'submit' ? 'Submitting…' : 'Submit decisions'}
        </button>
        <button
          type="button"
          onClick={() => void rejectProposal()}
          disabled={!!busy}
          className="rounded border border-red-300 bg-white px-3 py-1.5 text-sm text-red-800 hover:bg-red-50 disabled:opacity-50"
        >
          {busy === 'reject' ? 'Rejecting…' : 'Reject proposal'}
        </button>
      </div>
    ) : null}
    {showAbandonUi ? (
      <div className="flex flex-wrap gap-2 border-t border-gray-100 px-3 py-3">
        <p className="w-full text-xs text-gray-600">
          Cancels queued Marlin rows only. Orders already at Schwab may still fill — check your
          brokerage.
        </p>
        <button
          type="button"
          onClick={() => void rejectProposal()}
          disabled={!!busy}
          className="rounded border border-red-300 bg-white px-3 py-1.5 text-sm text-red-800 hover:bg-red-50 disabled:opacity-50"
        >
          {busy === 'reject' ? 'Abandoning…' : 'Abandon execution'}
        </button>
      </div>
    ) : null}
    {proposal.status === 'proposed' && autoApproveEffective ? (
      <p className="border-t border-gray-100 px-3 py-2 text-xs text-gray-500">
        Automatic execution is on — orders submit when the proposal is created.
      </p>
    ) : null}
  </div>
);

export default Tier1ExecutionWorkbench;
